(function () {
  'use strict';

  var API_BASE = 'https://api.geostock.app/shop/unimarketyyc.myshopify.com/variant_locations/';
  var checkGeneration = 0;
  var debounceTimer = null;
  var DEBUG = /[?&]ibl-debug=true/.test(window.location.search);
  var SOFT_FALLBACK_TEXT = 'Stock will be confirmed at checkout.';
  var LOCATION_STOCK_LIMIT_TEXT = 'Location stock is limited. Please adjust your cart before checkout.';
  var CHECKOUT_STOCK_LIMIT_TEXT = 'Some cart items are not available at the nearest location.';
  var REQUEST_TIMEOUT_MS = 2500;
  var RETRY_DELAY_MS = 800;
  var MAX_REQUEST_ATTEMPTS = 3;

  function log(label, data) {
    if (!DEBUG) return;
    console.group('%c[IBL Cart] ' + label, 'color: #d97706; font-weight: bold');
    if (data !== undefined) {
      if (typeof data === 'object') {
        console.table ? console.table(data) : console.log(data);
      } else {
        console.log(data);
      }
    }
    console.groupEnd();
  }

  function isValidVariantId(id) {
    return /^\d+$/.test(String(id));
  }

  function fetchInventoryOnce(variantId) {
    var controller = window.AbortController ? new AbortController() : null;
    var timeout = setTimeout(function () {
      if (controller) controller.abort();
    }, REQUEST_TIMEOUT_MS);

    return fetch(API_BASE + variantId, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) {
          log('fetchInventory — HTTP error', { variantId: variantId, status: response.status });
          return null;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.locations) {
          log('fetchInventory — no locations in response', { variantId: variantId, data: data });
          return null;
        }
        log('fetchInventory — received ' + data.locations.length + ' locations', { variantId: variantId });
        return data.locations;
      })
      .catch(function (err) {
        log('fetchInventory — network error', { variantId: variantId, error: String(err) });
        return null;
      })
      .finally(function () {
        clearTimeout(timeout);
      });
  }

  function fetchInventory(variantId) {
    if (!isValidVariantId(variantId)) {
      log('fetchInventory — INVALID variant ID', variantId);
      return Promise.resolve(null);
    }
    log('fetchInventory — requesting', { variantId: variantId, url: API_BASE + variantId });

    function attemptFetch(attempt) {
      return fetchInventoryOnce(variantId).then(function (locations) {
        if (locations) return locations;

        if (attempt >= MAX_REQUEST_ATTEMPTS) {
          log('fetchInventory — failed after all attempts', { variantId: variantId, attempts: attempt });
          return null;
        }

        log('fetchInventory — retrying after ' + RETRY_DELAY_MS + 'ms', {
          variantId: variantId,
          nextAttempt: attempt + 1,
          maxAttempts: MAX_REQUEST_ATTEMPTS
        });

        return new Promise(function (resolve) {
          setTimeout(function () {
            resolve(attemptFetch(attempt + 1));
          }, RETRY_DELAY_MS);
        });
      });
    }

    return attemptFetch(1);
  }

  function buildLocationItems(locations) {
    var sorted = locations.slice().sort(function (a, b) {
      var aDist = (a.distance != null) ? a.distance : Infinity;
      var bDist = (b.distance != null) ? b.distance : Infinity;
      if (aDist === bDist) return 0;
      return aDist - bDist;
    });

    if (DEBUG) {
      log('Locations (sorted by distance)', sorted.map(function (loc) {
        return {
          name: loc.name,
          distance: loc.distance != null ? loc.distance + ' km' : 'N/A',
          stock: loc.stock != null ? loc.stock : 0,
          enabled: loc.enabled,
          id: loc.id
        };
      }));
    }

    var items = [];
    var nearestStock = 0;
    for (var i = 0; i < sorted.length; i++) {
      var loc = sorted[i];
      if (!loc.enabled) continue;

      var stock = loc.stock ? loc.stock : 0;
      if (items.length === 0) nearestStock = stock;

      var li = document.createElement('li');

      var nameSpan = document.createElement('span');
      nameSpan.className = 'location';
      nameSpan.textContent = loc.name;
      li.appendChild(nameSpan);

      if (loc.distance != null) {
        var distSpan = document.createElement('span');
        distSpan.className = 'distance';
        distSpan.textContent = '(' + loc.distance + ' km)';
        li.appendChild(distSpan);
      }

      var sep = document.createTextNode(': ');
      li.appendChild(sep);

      var stockStrong = document.createElement('strong');
      stockStrong.className = 'stockCount';
      stockStrong.textContent = stock + ' in stock';
      li.appendChild(stockStrong);

      items.push(li);
    }
    return { items: items, nearestStock: nearestStock };
  }

  function checkCartItem(cartItem, gen) {
    var block = cartItem.querySelector('[data-ibl-product-block]');
    if (!block) return Promise.resolve(null);

    var variantId = block.getAttribute('data-variant-id');
    if (!variantId) return Promise.resolve(null);

    var productTitle = cartItem.querySelector('.cart-item__title');
    var titleText = productTitle ? productTitle.textContent.trim() : 'unknown';

    log('checkCartItem — start', { variantId: variantId, product: titleText });

    var loader = block.querySelector('.inventory-by-location-loader');
    var errorEl = block.querySelector('.inventory-by-location-error');
    var listEl = block.querySelector('ul[data-ibl-inventory]');

    if (loader) loader.style.display = '';
    if (errorEl) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
      errorEl.classList.remove('ibl-soft');
    }
    if (listEl) {
      listEl.style.display = 'none';
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
    }

    return fetchInventory(variantId).then(function (locations) {
      // Stale — a newer checkAllItems was called while fetch was in-flight
      if (checkGeneration !== gen) {
        log('checkCartItem — STALE response discarded', { variantId: variantId, gen: gen, currentGen: checkGeneration });
        return null;
      }

      try {
        if (loader) loader.style.display = 'none';

        if (!locations || locations.length === 0) {
          log('checkCartItem — no locations, allowing checkout with soft fallback', { variantId: variantId, product: titleText });
          if (errorEl) {
            errorEl.textContent = SOFT_FALLBACK_TEXT;
            errorEl.classList.add('ibl-soft');
            errorEl.style.display = '';
          }
          return null;
        }

        var result = buildLocationItems(locations);
        var items = result.items;
        var nearestStock = result.nearestStock;

        var qtyInput = cartItem.querySelector('.qty-input__input');
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;

        var decision;

        if (items.length === 0) {
          if (errorEl) {
            errorEl.textContent = SOFT_FALLBACK_TEXT;
            errorEl.classList.add('ibl-soft');
            errorEl.style.display = '';
          }
          if (listEl) listEl.style.display = 'none';
          decision = 'SOFT_FALLBACK';
        } else if (nearestStock === 0) {
          if (errorEl) {
            errorEl.textContent = LOCATION_STOCK_LIMIT_TEXT;
            errorEl.style.display = '';
          }
          if (listEl) listEl.style.display = 'none';
          decision = 'LOCATION_STOCK_LIMITED';
          return titleText + ': out of stock at nearest location';
        } else if (qty > nearestStock) {
          if (errorEl) {
            errorEl.textContent = LOCATION_STOCK_LIMIT_TEXT;
            errorEl.style.display = '';
          }
          if (listEl) listEl.style.display = 'none';
          decision = 'LOCATION_STOCK_LIMITED (need ' + qty + ', have ' + nearestStock + ')';
          return titleText + ': only ' + nearestStock + ' available at nearest location';
        } else {
          // Stock is OK — show the inventory list
          if (listEl) {
            for (var i = 0; i < items.length; i++) {
              listEl.appendChild(items[i]);
            }
            listEl.style.display = '';
          }
          if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
          }
          decision = 'OK';
        }

        log('Cart item decision', {
          product: titleText,
          variantId: variantId,
          nearestStock: nearestStock,
          cartQty: qty,
          decision: decision,
          totalLocations: items.length
        });

        return null;
      } catch (e) {
        log('checkCartItem — ERROR', { variantId: variantId, error: String(e) });
        if (errorEl) {
          errorEl.textContent = SOFT_FALLBACK_TEXT;
          errorEl.classList.add('ibl-soft');
          errorEl.style.display = '';
        }
        return null;
      }
    });
  }

  function setCheckoutButtonState(errors) {
    var checkoutButton = document.querySelector('#cart-summary button[name="checkout"], button[name="checkout"]:not([form])');
    var additionalCheckoutButtons = document.querySelectorAll('.additional-checkout-buttons [role="button"]');
    var errorDivId = 'inventory-checkout-error';

    var existingErrorDiv = document.getElementById(errorDivId);
    if (existingErrorDiv) {
      existingErrorDiv.remove();
    }

    if (!checkoutButton) return;

    if (!checkoutButton.dataset.originalText) {
      checkoutButton.dataset.originalText = checkoutButton.textContent;
    }

    checkoutButton.classList.add('ibl-ready');

    if (errors.length > 0) {
      checkoutButton.disabled = true;
      additionalCheckoutButtons.forEach(function (btn) {
        btn.style.display = 'none';
      });

      var errorDiv = document.createElement('div');
      errorDiv.id = errorDivId;
      errorDiv.textContent = CHECKOUT_STOCK_LIMIT_TEXT;
      checkoutButton.insertAdjacentElement('afterend', errorDiv);

      log('Checkout button — DISABLED by GeoStock', { inventoryIssues: errors });
      return;
    }

    checkoutButton.disabled = false;
    additionalCheckoutButtons.forEach(function (btn) {
      btn.style.display = 'flex';
    });

    log('Checkout button — ENABLED', { inventoryIssues: errors });
  }

  function checkAllItems() {
    var gen = ++checkGeneration;

    var cartItems = document.querySelectorAll('.cart-item');
    log('checkAllItems — start', { generation: gen, itemCount: cartItems.length });

    if (cartItems.length === 0) {
      log('checkAllItems — empty cart, enabling checkout');
      setCheckoutButtonState([]);
      return Promise.resolve();
    }

    var promises = [];
    for (var i = 0; i < cartItems.length; i++) {
      promises.push(checkCartItem(cartItems[i], gen));
    }

    return Promise.all(promises).then(function (results) {
      if (checkGeneration !== gen) {
        log('checkAllItems — STALE results discarded', { gen: gen, currentGen: checkGeneration });
        return;
      }

      var errors = [];
      for (var i = 0; i < results.length; i++) {
        if (results[i]) errors.push(results[i]);
      }

      log('checkAllItems — complete', {
        generation: gen,
        totalItems: results.length,
        errors: errors.length > 0 ? errors : 'none',
        checkoutAllowed: errors.length === 0
      });

      setCheckoutButtonState(errors);
    });
  }

  function scheduleCheck() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      checkAllItems();
    }, 300);
  }

  function init() {
    var checkoutButton = document.querySelector('#cart-summary button[name="checkout"], button[name="checkout"]:not([form])');
    if (checkoutButton) {
      checkoutButton.disabled = true;
      checkoutButton.classList.add('ibl-ready');
    }
    log('init — starting inventory check');
    checkAllItems();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('on:line-item:change', function () {
    log('Event: on:line-item:change — scheduling check');
    scheduleCheck();
  });

  document.addEventListener('on:cart:add', function () {
    log('Event: on:cart:add — scheduling check');
    scheduleCheck();
  });

  document.addEventListener('input', function (event) {
    if (!event.target.matches('.qty-input__input')) return;
    log('Event: quantity input changed — scheduling check');
    scheduleCheck();
  });

  if (DEBUG) {
    log('inventory-by-location-cart.js loaded', {
      hint: 'Add ?ibl-debug=true to URL to see these logs',
      apiBase: API_BASE
    });
  }
})();
